// src/api/analysis.js
import api from './index'; // Stelle sicher, dass der Import korrekt ist

/**
 * Get relevant laws for a topic
 * @param {Object} data - Request data
 * @param {string} data.topic - Legal topic/question
 * @returns {Promise<Object>} - Relevant laws data
 */
export const analyzeLaws = async (data) => {
  const response = await api.post('/analyze/laws', data);
  return response.data;
};

/**
 * Check if a law exists
 * @param {Object} data - Request data
 * @param {string} data.law - Law abbreviation to check
 * @returns {Promise<Object>} - Law existence check result
 */
export const checkLawExists = async (data) => {
  const response = await api.post('/check-law', data);
  return response.data;
};

/**
 * Get relevant references for a topic and laws and start synthesis automatically
 * @param {Object} data - Request data
 * @param {string} data.topic - Legal topic/question
 * @param {Array<string>} data.law_list - List of relevant laws
 * @param {string|number} [data.chatId] - Optional chat ID
 * @returns {Promise<Object>} - Relevant references data with synthesis pipeline info
 */
export const analyzeReferences = async (data) => {
  const response = await api.post('/analyze/references', data);
  return response.data;
};

/**
 * Check the status of a synthesis job
 * @param {string|number} chatId - Chat ID where the synthesis is being processed
 * @returns {Promise<Object>} - Synthesis status and result if completed
 */
export const checkSynthesisStatus = async (chatId) => {
  // NEU: Fehlerbehandlung für 404 hinzugefügt
  if (!chatId) {
    // Sollte nicht passieren, wenn von useQuery mit enabled: !!chatId aufgerufen
    return { status: 'error', message: 'Chat ID is required' };
  }
  try {
    const response = await api.get(`/analyze/synthesis-status/${chatId}`);
    return response.data; // Erwartet { status: '...', ... }
  } catch (error) {
    // Prüfen, ob es ein Axios-Fehler mit einer Response ist
    if (error.response && error.response.status === 404) {
      // Wenn der Job nicht gefunden wurde (404), geben wir einen "unknown" Status zurück
      console.log(`No synthesis job found for chat ${chatId} via status check.`);
      return {
        status: 'unknown',
        chatId: chatId,
        message: 'No synthesis job found for this chat',
      };
    }
    // Andere Fehler (Netzwerk, Server 500 etc.) werden vom Interceptor behandelt,
    // aber wir können hier trotzdem einen Fehlerstatus zurückgeben oder den Fehler weiterwerfen.
    // Das Weiterwerfen würde den useQuery in den 'error' Status versetzen.
    // Ein Fehlerstatus-Objekt zurückzugeben, hält den Query im 'success' Status.
    // Entscheide dich für eine Variante. Hier geben wir ein Fehlerobjekt zurück:
    console.error('Error fetching synthesis status:', error);
    return {
      status: 'error',
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch synthesis status',
    };
    // Alternative: Fehler weiterwerfen, damit useQuery isError wird
    // throw error;
  }
};

/**
 * Manually start synthesis (only needed if automatic start fails)
 * @param {Object} data - Request data
 * @param {string|number} data.chatId - Chat ID
 * @returns {Promise<Object>} - Status of the started synthesis job
 */
export const startSynthesis = async (data) => {
  const response = await api.post('/analyze/start-synthesis', data);
  return response.data;
};

/**
 * Get a section PDF
 * @param {Object} data - Request data
 * @param {string} data.citation - Legal citation (e.g., "ABGB §1053")
 * @returns {Promise<Object>} - PDF data or status
 */
export const getSectionPdf = async (data) => {
  const response = await api.post('/section-pdf', data);
  return response.data;
};

/**
 * Get a judgment PDF
 * @param {Object} data - Request data
 * @param {string} data.citation - Judgment citation (e.g., "OGH 5Ob104/13w")
 * @returns {Promise<Object>} - PDF data
 */
export const getJudgmentPdf = async (data) => {
  const response = await api.post('/judgment-pdf', data);
  return response.data;
};

/**
 * Check if a statement can be derived from given PDF sources
 * @param {Object} data - Request data
 * @param {string} data.statement - The statement to validate
 * @param {Array<string>} data.pdfPaths - Array of PDF paths to check against
 * @returns {Promise<Object>} - Derivation check result with canBeDerived status
 */
export const checkStatementDerivation = async (data) => {
  const response = await api.post('/can-statement-be-derived', data);
  return response.data;
};

/**
 * Poll for section PDF status until completed
 * @param {string} citation - Legal citation
 * @param {Function} onComplete - Callback when PDF is ready
 * @param {number} interval - Polling interval in ms
 * @param {number} maxAttempts - Maximum number of polling attempts
 */
export const pollForSectionPdf = async (
  citation,
  onComplete,
  interval = 3000,
  maxAttempts = 20
) => {
  let attempts = 0;

  const poll = async () => {
    attempts++;
    try {
      // Annahme: Es gibt einen Endpunkt /section-status/:citation
      const response = await api.get(
        `/section-status/${encodeURIComponent(citation)}`
      );
      const result = response.data;

      if (result.status === 'completed' && result.path) {
        onComplete(result.path);
        return;
      }

      if (attempts >= maxAttempts) {
        // Hier könntest du eine Fehlerbehandlung hinzufügen, z.B. onComplete(null, new Error('...'))
        console.error('Maximum polling attempts reached for section PDF');
        // onComplete(null, new Error('Maximum polling attempts reached')); // Optional
        return;
      }

      setTimeout(poll, interval);
    } catch (error) {
      console.error('Error polling for section PDF:', error);
      if (attempts < maxAttempts) {
        setTimeout(poll, interval);
      } else {
        // Optional: Fehlerbehandlung nach max Versuchen auch bei Fehlern
        // onComplete(null, error);
      }
    }
  };

  poll();
};
